<template>
  <div data-view="archived-threads">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>
    <layout-logged-frame title="Archived deals" has-before>
      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="[null, pagination.totalItems]"
        />
      </template>

      <template v-slot:content>
        <Filters
          is-archived
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <ThreadsTable
          :threads="threads"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          disable-select
          has-custom-items-template
          :has-assign-action="false"
          :on-select-item="onSelectItem"
          :on-change-item="onChangeItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :on-toggle-comments="onToggleThreadComments"
          :on-update-thread="getThreads"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import Filters from '../../components/forms/filters/Threads';
  import ThreadsTable from '../../components/table/threads/index';
  import {
    isObj,
    extractNestedProp,
    isNonEmptyStr,
    isNum,
    isStr,
  } from '@/utils';
  import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import NavigationTabs from '../../components/NavigationTabs'
  import navigationTabs from '../../config/threads/navigationTabs';
  export default {
    mixins: [urlPaginationRestore],
    components: {
      Filters,
      ThreadsTable,
      NavigationTabs,
    },
    data() {
      return {
        navigationTabs,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'updated_at',
          totalItems: 0,
          search: '',
        },
        threads: [],
        totalPages: 0,
        query: {},
        isLoading: false,
      };
    },
    computed: {
      activeThread() {
        return this.$store.getters['threads/activeItem'];
      },
      loggedUserRole() {
        return this.$store.getters['auth/role'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      isBusinessContact() {
        return (
          this.loggedUserRole.slug === 'marketing_agent' ||
          this.loggedUserRole.slug === 'business_contact' ||
          this.loggedUserRole.slug === 'business_admin'
        );
      },
      canSeeFilters() {
        return this.$ability.can('see-filter', 'filter');
      },
      commentPermissions() {
        return this.$store.getters['threads/commentPermissions'];
      },
    },
    created() {
      if (this.commentPermissions.length === 0) {
        this.getCommentPermissions();
      }
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getThreads();
      },
      getCommentPermissions() {
        return this.$store.dispatch('threads/getCommentPermissions');
      },
      onChangeItem({ action, id }, thread) {
        const query = {
          id: thread.id,
        };

        if (action === 'user') {
          query.user = isNum(id) ? id : this.loggedUser.id;
        } else if (action === 'partnership_manager' || action === 'editor') {
          query.queue = action;
        } else if (action === 'comment') {
          this.$store.dispatch('threads/setActiveItem', { ...thread });
          return this.$router.push({ name: 'archived-thread-add-comment', params: { id: thread.id }, query: this.$route.query });
        } else if (action === 'deny') {
          this.$store.dispatch('threads/setActiveItem', { ...thread });
          return this.toggleDenyDialog();
        }

        return this.$store.dispatch('threads/assignThread', query).then(() => {
          this.getThreads();
        }).catch(err => err);
      },
      wrapQuery(options = {}) {
        const {
          countries,
          page = 1,
          order = 'desc',
          per_page = this.pagination.rowsPerPage,
          sort = this.pagination.sortBy,
          search = null,
          labels = [],
          business = -1,
          teamMembers = [],
          merchant_id = -1,
          category = '',
          date_filter = '',
          status = -1,
          accountTypes = [],
          priority = -1,
        } = options;

        return {
          countries,
          page,
          order,
          sort,
          search,
          labels,
          business,
          teamMembers,
          merchant_id,
          category,
          date_filter,
          per_page,
          status,
          accountTypes,
          priority,
        };
      },
      getThreads() {
        // eslint-disable-next-line prefer-const
        let { query } = this;

        const options = {
          ...query,
        };

        const { order } = options;

        this.isLoading = true;
        this.pagination.order = order;

        query = this.wrapQuery({
          ...options,
        });

        return this.$store
          .dispatch('threads/getArchivedItems', query)
          .then(({ data, meta }) => {
            this.pages = meta?.last_page || 0;
            // getArchivedItems swallows failures with .catch(err => err), so `data`
            // can be a non-array (the error object) on a failed request. Guard the
            // spread like Index.vue does, otherwise `[...data]` throws "not iterable".
            this.threads = Array.isArray(data) ? [...data] : [];
            this.pagination.page = meta?.current_page || 0;
            this.pagination.totalItems = meta?.total || 0;
            this.pagination.lastPage = meta?.last_page || 0;
            this.totalPages = meta?.last_page || 0;

            return data;
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      onToggleThreadComments(thread, onComplete = () => {}) {
        if (thread.commentsRetrieved) {
          return onComplete();
        }

        const query = {
          id: thread.id,
          filter: 'all',
        };

        return this.$store
          .dispatch('threads/getComments', query)
          .then((comments) => {
            this.setThreadComments(thread, comments);
            return onComplete();
          });
      },
      setThreadComments({ id }, comments) {
        const { threads } = this;

        this.threads = threads.map((thread) => ({
          ...thread,
          comments: thread.id === id ? comments : thread.comments,
          commentsRetrieved: thread.id === id || !!thread.commentsRetrieved,
        }));
      },
      onSelectItem(thread) {
        this.$store.dispatch('threads/setActiveItem', { ...thread });
        this.$router.push({ name: 'archived-thread-edit', params: { id: thread.id }, query: this.$route.query });
      },
      onUpdatePagination({ page, order, sortBy }) {
        this.pagination.sortBy = isNonEmptyStr(sortBy) ? sortBy : this.pagination.sortBy

        const { query } = this;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: this.pagination.sortBy,
        };

        this.query = nextQuery;

        this.runInitialFetch(() => this.getThreads());
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        this.query = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.getThreads();
      },
      onFilter(filters) {
        const searchQuery = this.getSearchQuery(filters);

        this.query.page = 1;

        this.query = {
          ...this.query,
          ...searchQuery,
        };

        this.runInitialFetch(() => this.getThreads());
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = { sort: sortBy };

        this.getThreads();
      },
      getSearchQuery({
         countries,
         search,
         labels,
         teamMembers,
         business,
         shop,
         category,
         dates,
         status,
         accountTypes,
      }) {
        const query = {};

        query.countries = countries;
        query.search = search;
        query.labels = labels?.map((l) => l.id) ?? [];
        query.business = isObj(business) ? business.id : -1;
        query.teamMembers = teamMembers;
        query.merchant_id = isObj(shop) ? shop.id : -1;
        query.category = isObj(category) ? category.code : '';
        query.date_filter = isStr(dates) ? dates : '';
        query.status = isNum(status) ? status : -1;
        query.accountTypes = accountTypes?.map(({ code }) => code) ?? [];

        return query;
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='archived-threads'] {
  .v-datatable {
    min-width: $table-min-width;
  }

  [data-component-group='thread-panels'][data-component='status'] {
    .trigger.view-url {
      margin-left: -$spacing-md !important;
    }

    .trigger.assign-user {
      margin-left: -$spacing-sm;
    }
  }
}
</style>
